import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
} from "@mui/material";
import { useState } from "react";
import { currentUser, grants } from "../backoffice.access_control";
import {
  AutovioCalendarEvent,
  canBeCanceled,
  canBeDeleted,
  canBeHistoricallyDeleted,
  isASFCourseSession,
  isCourseLesson,
  isPaidLesson,
  isPendingInvitation,
  isPracticalExam,
  isTheoryExam,
  isTheoryLesson,
  isVehicleEvent,
} from "../model/autovioCalendarEvents";
import { calendarEventTitle } from "../utils/calendar";
import { EventInfoCard } from "./EventDialog/EventInfoCard";
import { useRecordContext } from "react-admin";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { CancelAppointmentDialog } from "../misc/calendar/CancelAppointmentDialog";
import { useDialog } from "../hooks/useDialog";
import { DialogCloseButton } from "../misc/DialogCloseButton";
import { EditDrivingLessonForm } from "./EventDialog/EditDrivingLessonForm";
import { EditSimulatorDrivingLessonForm } from "./EventDialog/EditSimulatorDrivingLessonForm";
import { EditTheoryExamForm } from "./EventDialog/EditTheoryExamForm";
import { EventDialogProvider } from "../contexts/EventDialogContext";
import { EditOtherEventForm } from "./EventDialog/EditOtherEventForm";
import { isStudent } from "../providers/studentsProvider";
import { StudentTheoryLessonDialog } from "./EventDialog/StudentTheoryLessonDialog";
import { StudentASFCourseSessionDialog } from "./EventDialog/StudentASFCourseSessionDialog";
import { DialogTopRightActions } from "../misc/DialogTopRightActions";
import { useMenu } from "../hooks/useMenu";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FirestoreIcon from "@mui/icons-material/LocalFireDepartment";
import { firebaseApp } from "../firebase";
import { EditCourseLessonForm } from "./EventDialog/EditCourseLessonForm";
import { ConfirmDialog } from "./ConfirmDialog";
import { CalendarEventStatusField } from "../fields/CalendarEventStatusField";
import { CopyIdIntoClipboardMenuItem } from "../menu-items/CopyIdIntoClipboardMenuItem";
import { CopyVogelIdIntoClipboardMenuItem } from "../menu-items/CopyVogelIdIntoClipboardMenuItem";
import { DeleteCalendarEventDialog } from "../misc/calendar/DeleteCalendarEventDialog";
import { EditVehicleEventForm } from "./EventDialog/EditVehicleEventForm";
import { EditASFCourseSessionForm } from "./EventDialog/EditASFCourseSessionForm";
import { assertNever } from "../backoffice.utils";
import { TheoryLessonDialog } from "./EventDialog/TheoryLessonDialog";
import { ShortEventHistory } from "./EventDialog/ShortEventHistory";
import { EventChangeLog } from "./EventDialog/EventChangeLog";
import { TriggerPaymentDialog } from "../misc/calendar/TriggerPaymentDialog";
import { CourseLessonDialog } from "./EventDialog/CourseLessonDialog";

export function EventDialog(props: {
  event: AutovioCalendarEvent;
  isOpen: boolean;
  onClose: () => void;
  initialMode?: "view" | "edit";
}) {
  const { event, isOpen, onClose, initialMode } = props;
  const record = useRecordContext();

  if (!(event && isOpen)) {
    return null;
  }

  if (isTheoryLesson(event) && isStudent(record)) {
    return <StudentTheoryLessonDialog event={event} onClose={onClose} />;
  } else if (isASFCourseSession(event) && isStudent(record)) {
    return <StudentASFCourseSessionDialog event={event} onClose={onClose} />;
  } else if (isTheoryLesson(event)) {
    return <TheoryLessonDialog event={event} onClose={onClose} initialMode={initialMode} />;
  } else if (isCourseLesson(event)) {
    return <CourseLessonDialog event={event} onClose={onClose} initialMode={initialMode} />;
  } else {
    return <_EventDialog event={event} onClose={onClose} initialMode={initialMode} />;
  }
}

function _EventDialog(props: { event: AutovioCalendarEvent; onClose: () => void; initialMode?: "view" | "edit" }) {
  const { event, onClose, initialMode } = props;
  const triggerPaymentDialog = useDialog();
  const deleteDialog = useDialog();
  const cancelDialog = useDialog();
  // No view mode for curse lessons (APP-6070) ...
  const effectiveInitialMode = isCourseLesson(event) ? "edit" : (initialMode ?? "view");
  const [mode, setMode] = useState<"view" | "edit">(effectiveInitialMode);
  const buttons: React.ReactElement[] = [];
  const dialogs: React.ReactElement[] = [];
  if (mode === "view") {
    const showTriggerPaymentButton =
      grants.has("CalendarEvent:triggerPayment") &&
      event.type === "DrivingLesson" &&
      !(
        event.billingWhen ||
        event.endedAt ||
        event.paymentStatus ||
        event.price.amount === 0 ||
        event.student.rsvp !== "accepted"
      );
    if (showTriggerPaymentButton) {
      buttons.push(
        <Button
          key="triggerPaymentButton"
          variant="outlined"
          sx={{ height: "40px" /* ... same height as other buttons in dialog. */ }}
          startIcon={<CreditCardIcon />}
          onClick={() => triggerPaymentDialog.openDialog()}
        >
          Bezahlung auslösen
        </Button>,
      );
      dialogs.push(
        <TriggerPaymentDialog key="TriggerPaymentDialog" event={event} {...triggerPaymentDialog.dialogProps} />,
      );
    }
    const showEditButton = (() => {
      if (event.deleted) {
        return false;
      }
      if (isTheoryLesson(event)) {
        return grants.has("calendarEventEdit") || grants.has("TheoryLesson:edit");
      }
      if (isVehicleEvent(event)) {
        return grants.has("calendarEventEdit") || grants.has("VehicleEvent:edit");
      }
      if (grants.has("calendarEventEdit")) {
        return true;
      }
      const isInstructorsOwnEvent = event.instructorId && event.instructorId === currentUser?.uid;
      return isInstructorsOwnEvent;
    })();
    if (showEditButton) {
      buttons.push(
        <Button
          variant="outlined"
          sx={{ height: "40px" /* ... same height as other buttons in dialog. */ }}
          startIcon={<EditIcon />}
          onClick={() => setMode("edit")}
        >
          {`${isPendingInvitation(event) ? "Einladung" : "Termin"} bearbeiten`}
        </Button>,
      );
    }
    const showDeleteButton =
      canBeDeleted(event) &&
      (grants.has("calendarEventEdit") || (isVehicleEvent(event) && grants.has("VehicleEvent:delete")));
    if (showDeleteButton) {
      buttons.push(
        <Button
          variant="outlined"
          sx={{
            height: "40px" /* ... same height as other buttons in dialog. */,
            color: "destructiveAction.main",
            borderColor: "destructiveAction.main",
            "&:hover": {
              backgroundColor: "destructiveAction.light",
              borderColor: "destructiveAction.main",
            },
          }}
          startIcon={<DeleteIcon />}
          onClick={deleteDialog.openDialog}
        >
          {`${isPendingInvitation(event) ? "Einladung" : "Termin"} löschen`}
        </Button>,
      );
      dialogs.push(
        <DeleteCalendarEventDialog
          isOpen={deleteDialog.isDialogOpen}
          calendarEvent={event}
          onClose={(calendarEventDeleted) => {
            deleteDialog.closeDialog();
            if (calendarEventDeleted) {
              onClose();
            }
          }}
        />,
      );
    }
    const showCancelButton = grants.has("calendarEventEdit") && canBeCanceled(event);
    if (showCancelButton) {
      buttons.push(
        <Button
          variant="outlined"
          sx={{
            height: "40px" /* ... same height as other buttons in dialog. */,
            color: "destructiveAction.main",
            borderColor: "destructiveAction.main",
            "&:hover": {
              backgroundColor: "destructiveAction.light",
              borderColor: "destructiveAction.main",
            },
          }}
          startIcon={<CancelIcon />}
          onClick={cancelDialog.openDialog}
        >
          Termin absagen
        </Button>,
      );
      dialogs.push(
        <CancelAppointmentDialog
          isOpen={cancelDialog.isDialogOpen}
          calendarEvent={event}
          onClose={(appointmentCancelled) => {
            cancelDialog.closeDialog();
            if (appointmentCancelled) {
              onClose();
            }
          }}
        />,
      );
    }
  } else if (mode === "edit") {
    // Do not add any buttons/dialogs.
  } else {
    assertNever(mode);
  }

  return (
    <EventDialogProvider event={event}>
      <Dialog open onClose={onClose} maxWidth="lg" data-testid="EventDialog">
        <DialogTitle>
          <EventDialogHeader event={event} mode={mode} closeDialog={onClose} />
        </DialogTitle>
        <DialogCloseButton onClick={onClose} />
        <DialogContent>
          <EventDialogBody event={event} mode={mode} closeDialog={onClose} closeEditMode={() => setMode("view")} />
        </DialogContent>
        {buttons.length > 0 && (
          <DialogActions
            disableSpacing
            sx={{
              alignItems: { xs: "stretch", sm: "center" },
              flexDirection: { xs: "column", sm: "row" },
              flexWrap: { xs: "nowrap", sm: "wrap" },
              gap: 1,
              "& .MuiButton-root": { width: { xs: "100%", sm: "auto" } },
            }}
          >
            {buttons}
          </DialogActions>
        )}
      </Dialog>
      {dialogs}
    </EventDialogProvider>
  );
}

export const EventDialogHeader = (props: {
  event: AutovioCalendarEvent;
  mode: "view" | "edit";
  closeDialog: () => void;
}) => {
  const { event, mode, closeDialog } = props;
  const title =
    mode === "edit"
      ? `${isPendingInvitation(event) ? "Einladung" : "Termin"} bearbeiten`
      : calendarEventTitle(event, { omitPrefix: true });
  const { anchorEl, open, openMenu, closeMenu } = useMenu();
  const { dialogProps: deleteCourseLessonConfirmDialogProps, openDialog: openDeleteCourseLessonConfirmDialog } =
    useDialog();
  const historicalDeleteDialog = useDialog();
  const canDeleteCourseLesson = isCourseLesson(event) && grants.has("CourseLesson:delete") && !event.deleted;
  const canHistoricallyDelete = grants.has("CalendarEvent:historical:delete") && canBeHistoricallyDeleted(event);
  const showStatus =
    mode === "view" && (isPaidLesson(event) || isTheoryExam(event) || isPracticalExam(event) || event.deleted);
  const showMenu = grants.has("developer") || canDeleteCourseLesson || canHistoricallyDelete;

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={3}>
      {showStatus ? (
        <div>
          <CalendarEventStatusField record={event} component="span" color="text.secondary" tooltip={false} />
          <br />
          <span>{title}</span>
        </div>
      ) : (
        <span>{title}</span>
      )}
      {showMenu && (
        <DialogTopRightActions>
          <IconButton key="moreButton" onClick={openMenu}>
            <MoreVertIcon sx={{ color: "text.secondary" }} />
          </IconButton>
          <Menu
            key="moreMenu"
            anchorEl={anchorEl}
            open={open}
            onClose={closeMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            MenuListProps={{ "aria-labelledby": "dialog-actions" }}
          >
            {grants.has("developer") && (
              <>
                <MenuItem
                  onClick={() => {
                    closeMenu();
                    const { projectId } = firebaseApp.options;
                    const path = `calendar_events/${event.id}`;
                    const url = `https://console.firebase.google.com/project/${projectId}/firestore/data/${path.replaceAll("/", "~2F")}`;
                    window.open(url, "_blank");
                  }}
                >
                  <ListItemIcon>
                    <FirestoreIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Öffne in Firestore</ListItemText>
                </MenuItem>
                <CopyIdIntoClipboardMenuItem record={event} closeMenu={closeMenu} />
                <CopyVogelIdIntoClipboardMenuItem record={event} closeMenu={closeMenu} />
              </>
            )}
            {canDeleteCourseLesson && (
              <MenuItem
                onClick={() => {
                  closeMenu();
                  openDeleteCourseLessonConfirmDialog();
                }}
              >
                <ListItemIcon>
                  <DeleteIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Löschen</ListItemText>
              </MenuItem>
            )}
            {canHistoricallyDelete && (
              <MenuItem
                onClick={() => {
                  closeMenu();
                  historicalDeleteDialog.openDialog();
                }}
              >
                <ListItemIcon>
                  <DeleteIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Termin löschen (nur AUTOVIO)</ListItemText>
              </MenuItem>
            )}
          </Menu>
          {canDeleteCourseLesson && (
            <ConfirmDialog
              {...deleteCourseLessonConfirmDialogProps}
              title="Termin löschen?"
              message="Bist du dir sicher, dass der Termin gelöscht werden soll? Diese Aktion kann nicht rückgängig gemacht werden."
              confirmButtonLabel="Ja, Termin löschen"
              onConfirm={async () => {
                await runSafely(() => dataProvider.delete("courseLessons", { id: event.id }), {
                  errorMessage: "Fehler beim Löschen des Termin.",
                  successMessage: "Termin erfolgreich gelöscht.",
                  onSuccess: closeDialog,
                });
              }}
            />
          )}
          {canHistoricallyDelete && (
            <DeleteCalendarEventDialog
              isOpen={historicalDeleteDialog.isDialogOpen}
              calendarEvent={event}
              onClose={(calendarEventDeleted) => {
                historicalDeleteDialog.closeDialog();
                if (calendarEventDeleted) {
                  closeDialog();
                }
              }}
            />
          )}
        </DialogTopRightActions>
      )}
    </Stack>
  );
};

const EventDialogBody = (props: {
  event: AutovioCalendarEvent;
  mode: "view" | "edit";
  closeDialog: () => void;
  closeEditMode: () => void;
}) => {
  const { event, mode, closeDialog, closeEditMode } = props;
  if (mode === "edit") {
    switch (event.type) {
      case "DrivingLesson":
        return event.drivingLessonType === "simulator" ? (
          <EditSimulatorDrivingLessonForm event={event} closeEditMode={closeEditMode} />
        ) : (
          <EditDrivingLessonForm event={event} closeEditMode={closeEditMode} />
        );
      case "CourseLesson":
        return <EditCourseLessonForm event={event} closeDialog={closeDialog} />;
      case "TheoryExam":
        return <EditTheoryExamForm event={event} closeEditMode={closeEditMode} />;
      case "Other":
        return <EditOtherEventForm event={event} closeEditMode={closeEditMode} />;
      case "VehicleEvent":
        return <EditVehicleEventForm event={event} closeEditMode={closeEditMode} />;
      case "ASFCourseSession":
        return <EditASFCourseSessionForm event={event} closeEditMode={closeEditMode} />;
      case "TheoryLesson":
      case "RemoteLesson":
      case "Unknown":
        return null;
      default:
        return assertNever(event);
    }
  }
  return (
    <Stack direction="column" spacing="1em">
      <Box>
        <ShortEventHistory event={event} />
        <EventInfoCard event={event} showStartedAtEndedAt={grants.has("viewAdditionalEventDetails")} />
      </Box>
      {event.updatedAt && grants.has("viewCalendarEventEditHistory") && <EventChangeLog event={event} />}
    </Stack>
  );
};
